/**
 * recitalPlanModel — the pure logic behind the Recital planning page.
 *
 * DECIDED 2026-08-06 (Anthony, on card c4sG8pdSIKNTAY4Plveu). The set list is an
 * ORDERED ARRAY ON THE RECITAL `events` DOC:
 *
 *   setList: [{ canonicalSongId, title, performers: [studentId], order }]
 *
 * — atomic reordering, no new collection, no new rules, no composite index.
 * `title` is a per-row DISPLAY string seeded from the `songs` registry and
 * editable: a printed program is a point-in-time artifact whose titles get
 * hand-styled, so identity lives in `canonicalSongId` and presentation in
 * `title`. Nothing here may treat `title` as a key.
 *
 * A REHEARSAL STORES ONLY WHICH SONGS ARE PLACED ON IT (`songIds`), never who
 * attends. The roster is DERIVED at render time — the union of the performers of
 * its placed songs — for the same reason project progress is computed rather
 * than stored: a set-list edit must not be able to leave a stale roster behind.
 * `deriveRehearsalRoster` is that derivation and it is the ONLY way a roster
 * should ever be produced. If you find yourself writing a roster to Firestore,
 * the decision you are overturning is this one.
 *
 * Everything in this file is pure: arrays in, new arrays out, no Firestore, no
 * clock. That is what makes the ordering and derivation testable without a
 * browser — the parts that DO need a browser are layout, and they get the screen.
 */

/** A set list read off an event doc, defensively: normalized, ordered, indexed 0..n-1. */
export const normalizeSetList = (setList) =>
	(Array.isArray(setList) ? setList : [])
		.filter((row) => row && row.canonicalSongId)
		.slice()
		.sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
		.map((row, i) => ({
			canonicalSongId: row.canonicalSongId,
			title: row.title || '',
			performers: Array.isArray(row.performers) ? row.performers.filter(Boolean) : [],
			order: i,
		}));

export const hasSong = (setList, canonicalSongId) =>
	normalizeSetList(setList).some((row) => row.canonicalSongId === canonicalSongId);

/**
 * Append a song to the program.
 *
 * ONE ROW PER SONG: a duplicate would give two rows the same identity, and every
 * consumer here (the rehearsal placement, the derived roster, a future merge
 * sweep) keys on canonicalSongId. Returns the list unchanged when the song is
 * already on it — the caller checks `hasSong` to say so on screen rather than
 * failing silently.
 */
export const addSongToSetList = (setList, { canonicalSongId, title = '', performers = [] }) => {
	const rows = normalizeSetList(setList);
	if (!canonicalSongId || rows.some((r) => r.canonicalSongId === canonicalSongId)) return rows;
	return [...rows, { canonicalSongId, title, performers: performers.filter(Boolean), order: rows.length }];
};

export const removeSetListRow = (setList, index) => {
	const rows = normalizeSetList(setList);
	if (index < 0 || index >= rows.length) return rows;
	return rows.filter((_, i) => i !== index).map((row, i) => ({ ...row, order: i }));
};

/**
 * Move a row up (-1) or down (+1). Out-of-range moves are no-ops rather than
 * errors: the first row's "up" control is disabled in the UI, but a keyboard or
 * a double-click must not be able to wrap the closing number to the top.
 */
export const moveSetListRow = (setList, index, delta) => {
	const rows = normalizeSetList(setList);
	const target = index + delta;
	if (index < 0 || index >= rows.length || target < 0 || target >= rows.length) return rows;
	const next = rows.slice();
	[next[index], next[target]] = [next[target], next[index]];
	return next.map((row, i) => ({ ...row, order: i }));
};

/** Edit one row's presentation or cast. `canonicalSongId` and `order` are not patchable. */
export const updateSetListRow = (setList, index, patch = {}) => {
	const rows = normalizeSetList(setList);
	if (index < 0 || index >= rows.length) return rows;
	return rows.map((row, i) => {
		if (i !== index) return row;
		const next = { ...row };
		if ('title' in patch) next.title = patch.title || '';
		if ('performers' in patch) {
			next.performers = (patch.performers || []).filter(Boolean);
		}
		return next;
	});
};

/**
 * Who is called to a rehearsal: the union of the performers of the songs placed
 * on it, in PROGRAM ORDER (first appearance wins), never stored.
 *
 * Program order rather than alphabetical on purpose — the list is read against
 * the running order on the night, so "who's in the first three numbers" is the
 * question it has to answer at a glance.
 *
 * A placed song that is no longer on the set list contributes nobody rather than
 * throwing: removing a song from the program is exactly the edit that must not
 * strand a rehearsal, and the stale id surfaces through
 * `orphanedSongIds` so the UI can offer to clear it.
 */
export const deriveRehearsalRoster = (setList, songIds = []) => {
	const rows = normalizeSetList(setList);
	const placed = new Set((songIds || []).filter(Boolean));
	const roster = [];
	const seen = new Set();
	for (const row of rows) {
		if (!placed.has(row.canonicalSongId)) continue;
		for (const studentId of row.performers) {
			if (seen.has(studentId)) continue;
			seen.add(studentId);
			roster.push(studentId);
		}
	}
	return roster;
};

/** Placed song ids that no longer exist on the set list — the UI offers to clear these. */
export const orphanedSongIds = (setList, songIds = []) => {
	const live = new Set(normalizeSetList(setList).map((r) => r.canonicalSongId));
	return (songIds || []).filter((id) => id && !live.has(id));
};

/**
 * Split the program across rehearsals — a convenience, NOT an engine (decision 2:
 * placement is manual; this is the first draft a human then drags around).
 *
 * CONTIGUOUS BLOCKS, not round-robin. A rehearsal that runs numbers 1–5 in order
 * is how a rehearsal is actually called; scattering every third number across
 * three rooms is arithmetically even and useless in a theatre. The remainder
 * goes to the EARLIEST rehearsals, which are the ones furthest from the show and
 * so the cheapest to overload.
 *
 * Returns a map of rehearsalId → songIds; existing placements are REPLACED,
 * which is why the UI confirms before calling it.
 */
export const splitEvenly = (setList, rehearsalIds = []) => {
	const rows = normalizeSetList(setList);
	const ids = (rehearsalIds || []).filter(Boolean);
	const out = {};
	if (!ids.length) return out;

	const base = Math.floor(rows.length / ids.length);
	let extra = rows.length % ids.length;
	let cursor = 0;
	for (const rehearsalId of ids) {
		const take = base + (extra > 0 ? 1 : 0);
		if (extra > 0) extra -= 1;
		out[rehearsalId] = rows.slice(cursor, cursor + take).map((r) => r.canonicalSongId);
		cursor += take;
	}
	return out;
};

/**
 * Performers proposed for a song, from the canonical join: every repertoire row
 * pointing at this registry doc is a student who has worked it.
 *
 * THE JOIN PROPOSES, THE HUMAN DISPOSES — the result seeds an editable field, it
 * is never the final cast. Unlinked repertoire rows are invisible here BY DESIGN
 * (card db8QjZ5uCpMi0EXPZ1kr is the remedy, and the invisibility is the incentive
 * to use it); do not add a title fallback to "help", or the program will quietly
 * fill with students who happen to have typed the same words.
 */
export const proposePerformers = (repertoireRows = []) => {
	const seen = new Set();
	const out = [];
	for (const row of repertoireRows) {
		const id = row && row.studentId;
		if (!id || seen.has(id)) continue;
		seen.add(id);
		out.push(id);
	}
	return out;
};

const recitalPlanModel = {
	normalizeSetList,
	hasSong,
	addSongToSetList,
	removeSetListRow,
	moveSetListRow,
	updateSetListRow,
	deriveRehearsalRoster,
	orphanedSongIds,
	splitEvenly,
	proposePerformers,
};

export default recitalPlanModel;
